// Faces on each frame of one animation. Frame 1 is the picture itself, so moving a face there changes the picture's own
// faces. On any later frame, the characters you move become that frame's hand-placed faces, and the frames in between
// follow along (see animFaces). "Watch" plays the frames at the animation's speed so you can spot where a fix is needed.
import { useEffect, useState } from "react";
import { animFaces, markById, setFramePlaced, setPictureFaces, type PlacedFace } from "../../../model/timeline";
import { Body, DoneFooter, Layer, Page, TopBar } from "../../common";
import { useTimeline } from "../TimelineLoader";
import { FacePlacing } from "./FacePlacing";

export function FrameFaces({ pictureId, motionId, onBack }: { pictureId: string; motionId: string; onBack: () => void }) {
  const { ep, update } = useTimeline();
  const mark = markById(ep, "picture", pictureId);
  const motion = mark?.motions?.find(m => m.id === motionId);
  const n = motion ? motion.frames.length + 1 : 0;
  const [i, setI] = useState(() => Math.min(1, Math.max(0, n - 1)));
  const [watching, setWatching] = useState(false);
  useEffect(() => {
    if (!watching || !motion) return;
    const timer = setInterval(() => setI(j => (j + 1) % n), 1000 / Math.max(1, motion.fps));
    return () => clearInterval(timer);
  }, [watching, n, motion?.fps]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!mark || !motion)
    return <Layer><Page className="tl-page"><TopBar onBack={onBack} title="Faces on each frame" /><Body><p className="hint">This animation is not there any more.</p></Body></Page></Layer>;

  const idx = Math.min(i, n - 1);
  const all = animFaces(mark, motion);
  const faces = all[idx] ?? [];
  const pic = idx === 0 ? mark.picture : motion.frames[idx - 1]!.picture;
  const fr = idx > 0 ? motion.frames[idx - 1]! : undefined;
  const hand = !!fr?.placed?.length;
  const src = (j: number) => (j === 0 ? mark.picture.src : motion.frames[j - 1]!.picture.src);

  function change(next: PlacedFace[]) {
    setWatching(false);
    if (idx === 0) return update(e => setPictureFaces(e, pictureId, next), { merge: `faces-${pictureId}` });
    const moved = next.filter(f => JSON.stringify(f) !== JSON.stringify(faces.find(g => g.char === f.char)));
    if (!moved.length) return;
    update(e => {
      const cur = markById(e, "picture", pictureId)?.motions?.find(m => m.id === motionId)?.frames[idx - 1]?.placed ?? [];
      return setFramePlaced(e, pictureId, motionId, idx, [...cur.filter(o => !moved.some(m => m.char === o.char)), ...moved]);
    }, { merge: `frame-${motionId}-${idx}` });
  }
  const go = (j: number) => { setWatching(false); setI(j); };

  return (
    <Layer>
      <Page dark className="tl-face-page">
        <TopBar onBack={onBack} title="Faces on each frame" sub="Move a face to fix it on that frame" />
        <FacePlacing ep={ep} t={mark.t} picture={pic} faces={faces} onFaces={change} fixedCast
          hint="Tap a face and drag it where it belongs on this frame."
          frames={{
            srcs: Array.from({ length: n }, (_, j) => src(j)), marked: Array.from({ length: n }, (_, j) => j > 0 && !!motion.frames[j - 1]!.placed?.length),
            index: idx, onIndex: go, watching, onWatch: setWatching,
            label: `Frame ${idx + 1} of ${n}${idx === 0 ? ": the picture itself" : hand ? ": placed by hand" : ""}`,
            onUndo: hand ? () => update(e => setFramePlaced(e, pictureId, motionId, idx, undefined)) : undefined,
            note: "Move a face on a few frames. The frames in between follow along.",
          }} />
        <DoneFooter onDone={onBack} />
      </Page>
    </Layer>
  );
}
